import type { Filter } from '@cubejs-client/core'
import { useQuery } from '@tanstack/react-query'
import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { useMemo, useState } from 'react'

import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import { useQueryState } from '@/hooks/use-query-state'
import { queryKeys } from '@/lib/query-keys'
import { hrFilters, hrMeasuresQuery, hrRowsQuery, mapHrRows, mapMeasures } from '@/services/cube/hr'
import { hrApi } from '@/services/http/hr'

/** URL'deki ortak İK filtreleri. */
export const FILTER_KEYS = [
  'firma',
  'sube',
  'durum',
  'kategori',
  'kaynak',
  'q',
  'from',
  'to',
] as const

export function useHrFilterState() {
  const query = useQueryState()
  return {
    query,
    firma: query.getList('firma'),
    sube: query.getList('sube'),
    durum: query.getList('durum'),
    kategori: query.getList('kategori'),
    kaynak: query.getList('kaynak'),
    q: query.get('q'),
    from: query.get('from'),
    to: query.get('to'),
  }
}

/** Kullanıcının firma kapsamı: filtre seçenekleri (kısıtlıysa yalnız kendi firmaları). */
export function useHrScope() {
  return useQuery({ queryKey: queryKeys.hr.scope(), queryFn: hrApi.scope, staleTime: 5 * 60_000 })
}

/** Ekran filtrelerinden Cube filtreleri (durum/kategori üyeleri sayfaya göre). */
export function useHrCubeFilters(
  view: string,
  {
    statusMember,
    categoryMember,
    sourceMember,
    dateMember,
    extra = [],
  }: {
    statusMember?: string
    categoryMember?: string
    sourceMember?: string
    dateMember?: string
    extra?: Filter[]
  },
) {
  const state = useHrFilterState()
  const equals: Record<string, string[]> = { firma: state.firma, sube: state.sube }
  if (statusMember) equals[statusMember] = state.durum
  if (categoryMember) equals[categoryMember] = state.kategori
  if (sourceMember) equals[sourceMember] = state.kaynak
  const key = JSON.stringify([
    ...hrFilters({ view, equals, search: state.q, dateMember, from: state.from, to: state.to }),
    ...extra,
  ])
  // İçerik aynıysa aynı dizi: sorgu anahtarları ve efektler her render'da yenilenmesin.
  return useMemo(() => JSON.parse(key) as Filter[], [key])
}

const PAGE_SIZE = 50

/** Sunucu taraflı (Cube) sayfalı ve sıralı İK listesi + ölçüler. */
export function useHrList<T>({
  view,
  members,
  rowKey,
  filters,
  defaultSort,
  measures = ['count'],
}: {
  view: string
  members: readonly string[]
  rowKey: string
  filters: Filter[]
  defaultSort: SortingState
  measures?: string[]
}) {
  const [sorting, setSorting] = useState<SortingState>(defaultSort)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  })
  const filterKey = JSON.stringify(filters)
  const [seenFilters, setSeenFilters] = useState(filterKey)
  if (seenFilters !== filterKey) {
    setSeenFilters(filterKey)
    setPagination((current) => ({ ...current, pageIndex: 0 }))
  }

  const rows = useCubeLiveQuery({
    queryKey: queryKeys.cube(view, { kind: 'rows', filters, sorting, pagination }),
    query: hrRowsQuery({
      view,
      members,
      filters,
      order: sorting,
      rowKey,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
    }),
    select: mapHrRows<T>(view, members),
    subscribe: false,
    refetchInterval: 60_000,
  })
  const totals = useCubeLiveQuery({
    queryKey: queryKeys.cube(view, { kind: 'measures', filters, measures }),
    query: hrMeasuresQuery(view, measures, filters),
    select: mapMeasures(view, measures),
    subscribe: false,
    refetchInterval: 60_000,
  })

  return {
    rows: rows.data ?? [],
    measures: totals.data ?? {},
    total: totals.data?.count ?? 0,
    sorting,
    onSortingChange: ((updater) => {
      setSorting((current) => (typeof updater === 'function' ? updater(current) : updater))
      setPagination((current) => ({ ...current, pageIndex: 0 }))
    }) satisfies OnChangeFn<SortingState>,
    pagination,
    setPagination,
    isLoading: rows.isLoading,
    busy: rows.isPlaceholderData,
    isError: rows.isError || totals.isError,
    error: rows.error ?? totals.error,
    refetch: () => {
      void rows.refetch()
      void totals.refetch()
    },
  }
}
